import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Button,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Timeline as HistoryIcon,
  EventNote as DateIcon,
  Person as PersonIcon,
  Description as NotesIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import Axios from "axios";
import useUserState from "../../hooks/useUserState";
import useDentalAccess from "../../hooks/useDentalAccess";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2, 0),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  chartItem: {
    marginBottom: theme.spacing(1),
  },
  chartHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  chartInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  typeChip: {
    fontWeight: "bold",
  },
  statusActive: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  statusInactive: {
    backgroundColor: "#fff3e0",
    color: "#ef6c00",
  },
  accordionDetails: {
    flexDirection: "column",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  detailIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  teethSummary: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  treatmentBadge: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.75rem",
    fontWeight: "bold",
  },
  timelineContainer: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    borderLeft: `2px solid ${theme.palette.divider}`,
  },
  timelineItem: {
    position: "relative",
    paddingBottom: theme.spacing(2),
    "&::before": {
      content: '""',
      position: "absolute",
      left: -6,
      top: 8,
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

const CHART_TYPES = {
  adult: { label: "Adulto", color: "primary" },
  child: { label: "Niño", color: "secondary" },
  mixed: { label: "Mixto", color: "default" },
};

function DentalChartHistory({ patientId, onSelectChart, selectedChartId }) {
  const classes = useStyles();
  const [userState] = useUserState();
  const dentalAccess = useDentalAccess();
  const { t } = useBDocTranslator();

  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);

  useEffect(() => {
    if (patientId && dentalAccess.canView) {
      fetchChartsHistory();
    }
  }, [patientId, dentalAccess.canView]);

  const fetchChartsHistory = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/patient/${patientId}/history`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      setCharts(response.data || []);
    } catch (error) {
      console.error("Error fetching charts history:", error);
      setCharts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (chartId) => (event, isExpanded) => {
    setExpandedChart(isExpanded ? chartId : null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "";
    try {
      return formatDistanceToNow(parseISO(dateString), {
        addSuffix: true,
        locale: es,
      });
    } catch {
      return "";
    }
  };

  const getTeethSummary = (dentalData) => {
    if (!dentalData || typeof dentalData !== "object") return {};

    const summary = {
      healthy: 0,
      caries: 0,
      filled: 0,
      extracted: 0,
      prosthesis: 0,
      crown: 0,
      implant: 0,
    };

    Object.values(dentalData).forEach((status) => {
      if (summary.hasOwnProperty(status)) {
        summary[status]++;
      }
    });

    return summary;
  };

  const renderTeethSummary = (dentalData) => {
    const summary = getTeethSummary(dentalData);
    const totalTeeth = Object.values(summary).reduce((a, b) => a + b, 0);

    if (totalTeeth === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t("Sin datos dentales registrados")}
        </Typography>
      );
    }

    return (
      <Box className={classes.teethSummary}>
        <Typography variant="subtitle2" gutterBottom>
          {t("Resumen de Dientes")} ({totalTeeth} {t("registrados")})
        </Typography>
        <Grid container spacing={1}>
          {Object.entries(summary).map(
            ([status, count]) =>
              count > 0 && (
                <Grid item key={status}>
                  <Box className={classes.treatmentBadge}>
                    {t(status)}: {count}
                  </Box>
                </Grid>
              )
          )}
        </Grid>
      </Box>
    );
  };

  if (!dentalAccess.canView) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="error">
            {dentalAccess.errorMessage}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.loadingContainer}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.root}>
      <CardContent>
        <div className={classes.header}>
          <Typography variant="h6" component="div">
            <HistoryIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
            {t("Historial de Odontogramas")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {charts.length} {t("registros encontrados")}
          </Typography>
        </div>

        {charts.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">
              {t("No hay odontogramas registrados para este paciente")}
            </Typography>
          </div>
        ) : (
          <div className={classes.timelineContainer}>
            {charts.map((chart, index) => (
              <div key={chart.id} className={classes.timelineItem}>
                <Accordion
                  expanded={expandedChart === chart.id}
                  onChange={handleAccordionChange(chart.id)}
                  className={classes.chartItem}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div
                      className={classes.chartHeader}
                      style={{ width: "100%" }}
                    >
                      <div className={classes.chartInfo}>
                        <Chip
                          label={
                            CHART_TYPES[chart.chart_type]?.label ||
                            chart.chart_type
                          }
                          color={
                            CHART_TYPES[chart.chart_type]?.color || "default"
                          }
                          size="small"
                          className={classes.typeChip}
                        />
                        <Chip
                          label={chart.is_active ? t("Activo") : t("Inactivo")}
                          size="small"
                          className={
                            chart.is_active
                              ? classes.statusActive
                              : classes.statusInactive
                          }
                        />
                        {chart.id === selectedChartId && (
                          <Chip
                            label={t("Seleccionado")}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </div>
                      <Typography variant="body2" color="textSecondary">
                        {formatRelativeTime(chart.created_at)}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <div className={classes.detailRow}>
                          <DateIcon className={classes.detailIcon} />
                          <div>
                            <Typography variant="body2" color="textSecondary">
                              {t("Fecha de Creación")}
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(chart.created_at)}
                            </Typography>
                          </div>
                        </div>

                        <div className={classes.detailRow}>
                          <PersonIcon className={classes.detailIcon} />
                          <div>
                            <Typography variant="body2" color="textSecondary">
                              {t("Último Modificado")}
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(chart.updated_at)}
                            </Typography>
                          </div>
                        </div>

                        {chart.notes && (
                          <div className={classes.detailRow}>
                            <NotesIcon className={classes.detailIcon} />
                            <div>
                              <Typography variant="body2" color="textSecondary">
                                {t("Notas")}
                              </Typography>
                              <Typography variant="body2">
                                {chart.notes}
                              </Typography>
                            </div>
                          </div>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        {renderTeethSummary(chart.dental_data)}
                      </Grid>
                    </Grid>

                    <Divider style={{ margin: "16px 0" }} />

                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => onSelectChart && onSelectChart(chart)}
                      >
                        {t("Ver Odontograma")}
                      </Button>
                      {dentalAccess.canEdit && chart.is_active && (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => onSelectChart && onSelectChart(chart)}
                        >
                          {t("Editar")}
                        </Button>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DentalChartHistory;
